import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { CONTACT_EMAIL, CONTACT_PHONE, ADDRESS } from '@/lib/constants';
import { trackEvent } from '@/lib/capi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const contactSchema = z.object({
  first_name: z.string().min(2, 'Name must be at least 2 characters'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.string().email('Please enter a valid email'),
  phone: z.string().optional(),
  message: z.string().optional(),
});

/* ── Left panel — email / phone / address / hours ── */
export function ContactInfoDetails() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(var(--c-purple-rgb),0.1)' }}>
          <Mail size={18} className="text-brand-blue" />
        </div>
        <div>
          <p className="text-sm text-brand-white font-medium">{CONTACT_EMAIL}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(var(--c-purple-rgb),0.1)' }}>
          <Phone size={18} className="text-brand-blue" />
        </div>
        <div>
          <p className="text-sm text-brand-white font-medium">{CONTACT_PHONE}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(var(--c-purple-rgb),0.1)' }}>
          <MapPin size={18} className="text-brand-blue" />
        </div>
        <div>
          <p className="text-sm text-brand-white font-medium">{ADDRESS}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(var(--c-purple-rgb),0.1)' }}>
          <Clock size={18} className="text-brand-blue" />
        </div>
        <div>
          <p className="text-sm text-brand-white font-medium">Mon–Fri, 8am–4pm AWST</p>
        </div>
      </div>
    </div>
  );
}

/* ── Right panel — the actual form + submit logic, shared by ContactPage and LandingPage ── */
export function ContactForm({
  formName = 'contact_page',
  submitLabel = 'Send Message',
  successTitle = 'Message Sent!',
  successText = "We'll be in touch within 24 hours.",
  showMessage = true,
  showSendAnother = true,
  successRedirect,
  smallPrint,
}) {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data) => {
    setSubmitError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, source: formName }),
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
      reset();
      // Tracking runs after the success state is set and gets its own try/catch:
      // a broken analytics stub (privacy extensions do this) must not make a
      // delivered enquiry look like a failed one to the visitor.
      try {
        if (window.gtag) {
          window.gtag('event', 'contact_form_submit', {
            'form_name': formName
          });
        }
        trackEvent('Lead', {
          eventParams: { form_name: formName },
          userData: {
            email: data.email,
            phone: data.phone,
            firstName: data.first_name,
            lastName: data.last_name,
          },
        });
      } catch (trackingErr) {
        // ignore — analytics failures must not affect the user-facing outcome
      }
      // The landing page conversion waits briefly for Google Ads to receive the
      // event before navigating. If the tag is unavailable, preserve the normal
      // successful-submit journey instead.
      if (successRedirect) {
        if (typeof window.gtagSendEvent === 'function') {
          window.gtagSendEvent(successRedirect);
        } else {
          navigate(successRedirect);
        }
      }
    } catch (err) {
      setSubmitError('Something went wrong. Please try again.');
      if (window.gtag) {
        window.gtag('event', 'contact_form_error', {
          'form_name': formName
        });
      }
    }
  };

  const inputClasses = "w-full rounded-xl px-4 py-3.5 text-sm text-brand-white placeholder:text-brand-muted/50 focus:outline-none focus:ring-1 focus:ring-brand-blue/40 transition-colors";
  const inputStyle = { backgroundColor: 'var(--brand-input-bg)', border: '1px solid var(--brand-glass-border)' };
  const labelClasses = "block text-sm font-medium text-brand-off-white mb-2";
  const errorClasses = "text-xs text-red-400 mt-1.5";

  return (
    <div className="glass-card p-8 md:p-10">
      {submitted ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-16"
          data-testid="contact-success"
        >
          <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={32} className="text-green-400" />
          </div>
          <h3 className="text-2xl font-bold text-brand-white mb-2">{successTitle}</h3>
          <p className={`text-brand-muted${showSendAnother ? ' mb-6' : ''}`}>{successText}</p>
          {showSendAnother && <button
            onClick={() => setSubmitted(false)}
            data-testid="contact-send-another"
            className="px-6 py-2.5 rounded-full text-brand-white text-sm font-medium hover:border-brand-blue-light transition-colors"
            style={{ border: '1px solid var(--brand-border)' }}
          >
            Send another message
          </button>}
        </motion.div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} data-testid="contact-form">
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${showMessage ? 'mb-6' : 'mb-8'}`}>
            <div>
              <label className={labelClasses}>First Name *</label>
              <input
                {...register('first_name')}
                data-testid="contact-first-name"
                className={inputClasses}
                style={inputStyle}
              />
              {errors.first_name && <p className={errorClasses}>{errors.first_name.message}</p>}
            </div>
            <div>
              <label className={labelClasses}>Last Name *</label>
              <input
                {...register('last_name')}
                data-testid="contact-last-name"
                className={inputClasses}
                style={inputStyle}
              />
              {errors.last_name && <p className={errorClasses}>{errors.last_name.message}</p>}
            </div>
            <div>
              <label className={labelClasses}>Email Address *</label>
              <input
                {...register('email')}
                type="email"
                data-testid="contact-email"
                className={inputClasses}
                style={inputStyle}
              />
              {errors.email && <p className={errorClasses}>{errors.email.message}</p>}
            </div>
            <div>
              <label className={labelClasses}>Phone Number</label>
              <input
                {...register('phone')}
                data-testid="contact-phone"
                className={inputClasses}
                style={inputStyle}
              />
            </div>
          </div>

          {showMessage && <div className="mb-8">
            <label className={labelClasses}>Message / Tell us about your use case</label>
            <textarea
              {...register('message')}
              rows={5}
              placeholder="Tell us about your business needs, current challenges, or what you're hoping to achieve with AI..."
              data-testid="contact-message"
              className={`${inputClasses} resize-none`}
              style={inputStyle}
            />
            {errors.message && <p className={errorClasses}>{errors.message.message}</p>}
          </div>}

          {submitError && (
            <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="contact-error">
              {submitError}
            </div>
          )}

          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={isSubmitting}
            data-testid="contact-submit-button"
            className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-full bg-brand-blue text-white font-semibold transition-all duration-300 shadow-[0_0_20px_rgba(var(--c-purple-rgb),0.3)] hover:shadow-[0_0_30px_rgba(var(--c-purple-rgb),0.5)] hover:bg-brand-blue-light disabled:opacity-60"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Sending...
              </>
            ) : (
              <>
                {submitLabel} <Send size={18} />
              </>
            )}
          </motion.button>
          {smallPrint && <p className="mt-4 text-center text-xs text-brand-muted">{smallPrint}</p>}
        </form>
      )}
    </div>
  );
}
